import type { CSSProperties, ReactNode } from 'react';
import { Icon } from './Icon';

// Kuun UI 통합안(After) — ActionCard ("지금 해야 할 일").
//
// KPI 카드는 정보만, ActionCard 는 다음 액션(CTA)까지 한 화면에. Dashboard
// hero 의 트리아지(접수 분류) 우선 영역에 사용.
//   - 좌측 5px 컬러바(상태색) + 큰 숫자(34/800, 상태색) + 라벨 + meta(mono)
//   - CTA 버튼 bg = 상태색, arrow-right Icon (이모지 금지 — `→` 문자 제거).
//   - 카드 radius var(--r-card) + var(--shadow-md). 하드코딩 hex 없음.

export type ActionCardVariant = 'primary' | 'secondary' | 'danger';

export interface ActionCardProps {
  /** 좌측 5px 컬러바 + 숫자·CTA 색 (상태색 토큰, 예: var(--severity-urgent)) */
  color: string;
  /** "긴급 리포트", "D-7 임박 계약" 등 */
  label: string;
  /** 큰 숫자 (또는 문자열 "5") */
  count: number | string;
  /** "건 미배정", "대 출고 직전" 등 */
  suffix?: string;
  /** 보조 텍스트 (가장 오래, 매장명 등) — mono font */
  age?: string;
  /** CTA 버튼 라벨 */
  ctaLabel: string;
  /** CTA variant (호환 유지 — 시각은 color 기반) */
  ctaVariant?: ActionCardVariant;
  /** CTA 클릭 핸들러 */
  onCta?: () => void;
  /** 좌측 상단 아이콘 node (Icon 컴포넌트 권장 — 이모지 금지) */
  icon?: ReactNode;
  /** CTA 비활성화 (count=0 이면 자동으로 disabled) */
  disabled?: boolean;
  /** 카드 클릭 시 (CTA 없을 때도 카드 자체 click 가능) */
  onClick?: () => void;
}

export function ActionCard({
  color,
  label,
  count,
  suffix,
  age,
  ctaLabel,
  onCta,
  icon,
  disabled,
  onClick,
}: ActionCardProps): JSX.Element {
  const isDisabled = disabled ?? (typeof count === 'number' && count === 0);

  const cardStyle: CSSProperties = {
    flex: 1,
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-card)',
    borderRadius: 'var(--r-card)',
    boxShadow: 'var(--shadow-md)',
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
    cursor: onClick && !isDisabled ? 'pointer' : 'default',
    opacity: isDisabled ? 0.55 : 1,
    fontFamily: 'var(--font-sans)',
  };

  function handleClick(): void {
    if (isDisabled) return;
    onClick?.();
  }
  function handleCta(e: React.MouseEvent): void {
    e.stopPropagation();
    if (isDisabled) return;
    onCta?.();
  }

  return (
    <div
      style={cardStyle}
      onClick={onClick ? handleClick : undefined}
      role={onClick ? 'button' : undefined}
      data-testid="action-card"
      aria-disabled={isDisabled}
    >
      <div style={{ width: 5, background: color, flexShrink: 0 }} aria-hidden />
      <div style={{ padding: '14px 16px', flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          {icon != null && (
            <span style={{ color, display: 'inline-flex', alignSelf: 'center' }}>{icon}</span>
          )}
          <span
            style={{
              fontSize: 34,
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
              color,
            }}
            data-testid="action-card-count"
          >
            {count}
          </span>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-default)' }}>{label}</span>
        </div>
        {(suffix || age) && (
          <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 6 }}>
            {suffix && <span>{suffix}</span>}
            {suffix && age ? ' · ' : ''}
            {age && <span style={{ fontFamily: 'var(--font-mono)' }}>{age}</span>}
          </div>
        )}
        <button
          type="button"
          onClick={handleCta}
          disabled={isDisabled}
          style={{
            marginTop: 12,
            height: 34,
            padding: '0 14px',
            border: 'none',
            borderRadius: 'var(--r-btn)',
            background: isDisabled ? 'var(--bg-active)' : color,
            color: isDisabled ? 'var(--fg-subtle)' : 'var(--brand-on)',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'var(--font-sans)',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            whiteSpace: 'nowrap',
          }}
          data-testid="action-card-cta"
        >
          {ctaLabel}
          <Icon name="arrow-right" size={15} color="var(--brand-on)" />
        </button>
      </div>
    </div>
  );
}
